/** TanStack Query hooks for the admin settings / consent-version surface (Plan 03-08). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import * as api from "./api";
import type {
  ComplianceConfigUpdate,
  ConsentLanguage,
  ConsentVersionCreate,
} from "./api";

const CONSENT_VERSIONS_KEY = "admin-consent-versions";
const COMPLIANCE_CONFIG_KEY = "admin-compliance-config";

/** List consent versions; `language` undefined = all languages. */
export function useConsentVersionsQuery(language?: ConsentLanguage) {
  return useQuery({
    queryKey: [CONSENT_VERSIONS_KEY, language ?? "all"],
    queryFn: () => api.listConsentVersions(language),
  });
}

/**
 * Create-and-activate a new consent version. Invalidates every consent-version
 * list on success (the activated row supersedes the prior active row at the
 * backend, so all language filters must refetch). Component surfaces the
 * success/error toast (mirrors the Plan 03-07 admin-candidates pattern).
 */
export function useCreateConsentVersionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ConsentVersionCreate) =>
      api.createConsentVersion(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [CONSENT_VERSIONS_KEY] });
    },
  });
}

// --- Compliance config (Plan 05-09) -----------------------------------------

/** The single compliance-config singleton (identity fields + bar_status + mode). */
export function useComplianceConfig() {
  return useQuery({
    queryKey: [COMPLIANCE_CONFIG_KEY],
    queryFn: () => api.getComplianceConfig(),
  });
}

/** PATCH structured compliance fields; refetch the singleton on success. */
export function useUpdateComplianceConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: ComplianceConfigUpdate) =>
      api.updateComplianceConfig(patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [COMPLIANCE_CONFIG_KEY] });
    },
  });
}

/**
 * Flip test<->live. Going live throws ApiError(422, detail:{error:'compliance_incomplete',
 * missing:[]}) when the config is not all-green — the component surfaces the missing list.
 */
export function useSwitchComplianceMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mode: "test" | "live") => api.switchComplianceMode(mode),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [COMPLIANCE_CONFIG_KEY] });
    },
  });
}
